import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';

// Simple DurationInput component to pick hours, minutes, seconds
const DurationInput = ({ value, onChange }) => {
  const { hours = 0, minutes = 0, seconds = 0 } = value || {};

  const handleChange = (field, val) => {
    let intVal = parseInt(val, 10);
    if (isNaN(intVal) || intVal < 0) intVal = 0;

    if (field === 'hours' && intVal > 23) intVal = 23;
    if ((field === 'minutes' || field === 'seconds') && intVal > 59) intVal = 59;

    const updated = { ...value, [field]: intVal };
    console.log(`DurationInput updated:`, updated);
    onChange(updated);
  };

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <input
        type="number"
        min="0"
        max="23"
        className="form-control"
        value={hours}
        onChange={e => handleChange('hours', e.target.value)}
        style={{ width: '50px' }}
        aria-label="Hours"
      />
      <span>h</span>
      <input
        type="number"
        min="0"
        max="59"
        className="form-control"
        value={minutes}
        onChange={e => handleChange('minutes', e.target.value)}
        style={{ width: '50px' }}
        aria-label="Minutes"
      />
      <span>m</span>
      <input
        type="number"
        min="0"
        className="form-control"
        max="59"
        value={seconds}
        onChange={e => handleChange('seconds', e.target.value)}
        style={{ width: '50px' }}
        aria-label="Seconds"
      />
      <span>s</span>
    </div>
  );
};

const AddItemStagingProcess = ({ show, onClose }) => {
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const [selectedOption, setSelectedOption] = useState(null);
  const [processName, setProcessName] = useState('');
  const [workingDuration, setWorkingDuration] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [workingHours, setWorkingHours] = useState({ hours: 0, minutes: 0, seconds: 0 });

  // Track if user tried to submit, to show validation messages
  const [submitted, setSubmitted] = useState(false);

  const timing_options = [
    { value: '0', label: 'Before Production' },
    { value: '1', label: 'During Production' },
    { value: '2', label: 'After Production' },
  ];

  const formatDuration = ({ hours, minutes, seconds }) =>
    `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;

  // Helper to check if a duration is empty (all zeros)
  const isDurationEmpty = ({ hours, minutes, seconds }) =>
    !(hours > 0 || minutes > 0 || seconds > 0);

  const handleSubmit = async () => {
    setSubmitted(true);

    if (!processName.trim()) {
      return;
    }
    if (!selectedOption) {
      return;
    }
    if (isDurationEmpty(workingHours)) {
      return;
    }
    if (isDurationEmpty(workingDuration)) {
      return;
    }

    const payload = {
      itemstagging_name: processName.trim(),
      timing: selectedOption.value,
      working_duration: formatDuration(workingDuration),
      working_hours: formatDuration(workingHours),
    };

    console.log('Payload to be sent:', payload);

    try {
      const response = await axios.post(`${baseURL}/itemstagging`, payload);
      console.log('Success:', response.data);
      onClose(); // close modal after success
    } catch (error) {
      console.error('Error:', error.response?.data || error.message);
      alert(error.response?.data?.message || 'Failed to create staging process');
    }
  };

  return (
    <Modal show={show} size="md" onHide={onClose}>
      <div className="modal-content">
        <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
          <h3 className="modal-title text-white">Create Staging Process</h3>
          <Button variant="" type="button" className="close px-6 py-2" onClick={onClose}>
            <span className="text-white">×</span>
          </Button>
        </div>
        <div className="modal-body">
          <div className="row">
            {/* Process Name */}
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Process Name<span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Process Name"
                value={processName}
                onChange={e => setProcessName(e.target.value)}
              />
              {submitted && !processName.trim() && (
                <span className="text-danger ms-1">This field is required</span>
              )}
            </div>

            {/* Staging Time */}
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Staging Time<span className="required">*</span>
              </label>
              <Select
                placeholder="Select Staging Time"
                value={selectedOption}
                onChange={setSelectedOption}
                options={timing_options}
              />
              {submitted && !selectedOption && (
                <span className="text-danger ms-1">This field is required</span>
              )}
            </div>

            {/* Pre/Post Production Duration */}
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Pre/Post Production Duration<span className="required">*</span>
              </label>
              <div className="d-block">
                <DurationInput value={workingHours} onChange={setWorkingHours} />
              </div>
              {submitted && isDurationEmpty(workingHours) && (
                <span className="text-danger ms-1">This field is required</span>
              )}
            </div>

            {/* Work Duration */}
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Work Duration<span className="required">*</span>
              </label>
              <div className="d-block">
                <DurationInput value={workingDuration} onChange={setWorkingDuration} />
              </div>
              {submitted && isDurationEmpty(workingDuration) && (
                <span className="text-danger ms-1">This field is required</span>
              )}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
            <Button className="btn btn-sm btn-primary" onClick={handleSubmit}>
              Create Staging Process
              <i className="fas fa-angle-double-right ms-2"></i>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddItemStagingProcess;
